'use client';

import type { EmblaCarouselType, EmblaOptionsType } from 'embla-carousel';
import useEmblaCarousel, { type EmblaViewportRefType } from 'embla-carousel-react';

import { useMediaQuery } from '@hooks/useMediaQuery';
import { cn } from '@utils/cn';

import { CarouselArrowNavigation } from './CarouselArrowNavigation';
import { CarouselDotNavigation } from './CarouselDotNavigation';

export type CarouselNavigationType = 'arrows' | 'dots' | 'none';

export type CarouselSizes = string | Partial<Record<string, string>>;

type CarouselProps = {
  carouselParent?: [EmblaViewportRefType, EmblaCarouselType | undefined];
  className?: string;
  controls?: {
    className?: string;
    isMobileVisible?: boolean;
    iconOnly?: boolean;
    type: CarouselNavigationType;
  };
  gap?: string;
  options?: EmblaOptionsType;
  slides: React.ReactNode[];
  slideSpacing?: CarouselSizes;
  slideSize?: CarouselSizes;
  viewportProps?: {
    className?: string;
  };
};

export function Carousel({
  carouselParent,
  controls,
  options,
  gap,
  slides,
  slideSize = '100%',
  className,
  viewportProps,
}: CarouselProps) {
  const [emblaRef, emblaApi] = useEmblaCarousel(options);
  const isMd = useMediaQuery('(width >= 48rem)');

  return (
    <section className={cn('group relative w-full h-full mx-auto', className)}>
      <CarouselArrowNavigation
        emblaApi={carouselParent?.[1] ?? emblaApi}
        isMobileVisible={controls?.isMobileVisible ?? false}
      />

      <div
        className={cn('overflow-hidden h-full', viewportProps?.className)}
        ref={carouselParent?.[0] ?? emblaRef}
      >
        <div
          className={cn('flex h-full touch-pan-y touch-pinch-zoom', gap ? `gap-${gap}` : 'gap-0')}
        >
          {slides.map((slide, index) => {
            const isSizeString = typeof slideSize === 'string';
            const isSizeObject = typeof slideSize === 'object';

            return (
              <div
                className={cn('flex flex-1')}
                key={`carousel-slide-${index}`}
                style={{
                  flex: isSizeString ? `0 0 ${slideSize}` : undefined,
                  minWidth:
                    slides.length > 3
                      ? isSizeObject
                        ? isMd
                          ? slideSize.md
                          : slideSize.xs
                        : undefined
                      : isMd
                        ? undefined
                        : '90%',
                }}
              >
                {slide}
              </div>
            );
          })}
        </div>
      </div>

      <CarouselDotNavigation
        emblaApi={carouselParent?.[1] ?? emblaApi}
        iconOnly={controls?.iconOnly}
        type={controls?.type}
      />
    </section>
  );
}
